import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { shallowEqual, useSelector } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { withRights } from 'providers/RightsConsumer';
import { addPropertyPrefix } from 'utils/propertyChangeAdapters';
import memoize from 'lodash.memoize';
import debounce from 'debounce-promise';
import { ApplicationFeaturesContext } from 'providers/ApplicationFeaturesContext';
import { RightsContext } from 'providers/RightsContext';
import { mandatoryDealerAssetFields, mandatoryFinancedAssetFields, mandatorySupplierAssetFields } from '../constants';
import {
  Grid,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
  Tooltip,
  Fab,
  Grow,
  Accordion as ExpansionPanel,
  AccordionSummary as ExpansionPanelSummary,
  AccordionDetails as ExpansionPanelDetails
} from '@mui/material';
import { ExpandMore, Build, History, List, Remove, FileCopy } from '@mui/icons-material';
import OfferAssetPartnerComponent from './OfferAssetPartnerComponent';
import OfferAssetPropertiesComponent from './OfferAssetPropertiesComponent';
import OfferAssetEquipmentsComponent from './OfferAssetEquipmentsComponent';
import OfferAssetFinancialComponent from './OfferAssetFinancialComponent';
import OfferAssetSecondHandComponent from './OfferAssetSecondHandComponent';
import OfferAssetChassisNumberComponent from './OfferAssetChassisNumberComponent';
import OfferAssetChassisNumberPopupContainer from './OfferAssetChassisNumberPopupContainer';
import OfferAssetFirstOwnerComponent from './OfferAssetFirstOwnerComponent';
import OfferAssetRematComponent from './OfferAssetRematComponent';
import AssetSearchComponent from 'features/asset/hierarchy/components/AssetSearchComponent';
import OfferAssetRequiredPropertiesComponent from './OfferAssetRequiredPropertiesComponent';
import withCustomComponent from 'providers/withCustomComponent';
import { COMBO, SEARCH, SEARCH_BIT, SELECT, TP_DATE, TP_NUM, TP_TEXT } from 'constants/inputTypes';
import { CollapseCard } from '@totalsoft/rocket-ui';
import { OfferAssetImage, OfferAssetOptionsWrapper, StickyGrid, StickyGridWrapper } from './OfferAssetStyles';
import assetHierarchyFunctions from 'features/asset/hierarchy/assetHierarchyFunctions';
import { getDateValue } from 'utils/functions';
import { supplierType } from '../constants';
import OfferAssetWorkingLocationComponent from './OfferAssetWorkingLocationComponent';
import { useWorkLocationMandatory } from '../hooks/useUserWorkLocations';

const addPartnerPrefix = memoize(addPropertyPrefix('partner'));
const addPropertiesPrefix = memoize(addPropertyPrefix('properties'));

const OfferAssetComponent = React.memo(
  ({
    t,
    loadDealers,
    loadSuppliers,
    loadPartnerContactInfo,
    currencies,
    loadPropertyDetails,
    saving,
    duplicating,
    onPropertyChange,
    onSave,
    onDuplicate,
    rights,
    assetImage,
    isDirty,
    asset,
    hierarchy,
    isSearchingRestrictedDealer,
    handleChassisNumberSave,
    updatingChassisNumber,
    handleCancelChassisNumberSave,
    localChassisNumber,
    updateAssetChassisNo,
    financedAssetWrite,
    openOptions,
    localSupplier,
    handleOpenOptions,
    handleCloseOptions,
    openConfigurator,
    openPropertiesHistory,
    maxMonths,
    selfPartnerName,
    enableConfiguratorButton,
    onAssetDataChange,
    processing,
    hasWorkingHours,
    isFacilityFrame,
    assetPropertiesAreLoaded,
    defaultValue
  }) => {
    const { enableAssetConfigurator } = useContext(ApplicationFeaturesContext);

    const theme = useTheme();
    const underLg = useMediaQuery(theme.breakpoints.down('lg'));

    const document = asset.document;
    const validationInfo = asset.validationInfo;
    const workLocationIsMandatory = useWorkLocationMandatory(document);
    const configurationButtonVisibleRule = enableAssetConfigurator && enableConfiguratorButton;
    const propertiesHistoryButtonVisibleRule = document.propertiesHistoryEnabled;
    const propertiesDuplicateVisibleRule = document.propertiesDuplicateEnabled;
    const assetChassisNumberButtonVisibleRule = !financedAssetWrite && updateAssetChassisNo;
    const actionsVisibleRules = configurationButtonVisibleRule || assetChassisNumberButtonVisibleRule || propertiesHistoryButtonVisibleRule;
    const [expandedCards, setExpandedCards] = useState({
      dealerCardExpanded: false,
      assetPropertiesCardExpanded: false
    });

    const secondHandProperty = hasWorkingHours
      ? {
          value: !document.isSecondHand && !document.workingHours ? defaultValue : document.workingHours,
          label: t('OfferEdit.Asset.Info.WorkingHours'),
          error: validationInfo.workingHours,
          disabled: document.category === 'New',
          visible: hasWorkingHours !== undefined,
          onChange: onPropertyChange('workingHours')
        }
      : {
          value: document.km,
          label: t('OfferEdit.Asset.Info.AcquisitionKm'),
          error: validationInfo.km,
          mandatory: Boolean(validationInfo.km) || (document.isSecondHand && !hasWorkingHours && !document.km),
          disabled: document.category === 'New',
          visible: hasWorkingHours !== undefined,
          onChange: onPropertyChange('km')
        };
    const { financedAssetDealerRead, financedAssetDealerWrite } = useContext(RightsContext);

    const actionRulesMap = new Map([
      ['offerAssetCard_assetActions_equipmentsConfigurator', configurationButtonVisibleRule],
      ['offerAssetCard_assetActions_propertiesHistory', propertiesHistoryButtonVisibleRule],
      ['OfferAssetChassisNumberPopupContainer', assetChassisNumberButtonVisibleRule]
    ]);
    const checkIfOnlyOneActionRuleIsVisible = useCallback(() => {
      let trueConditions = 0;
      if (configurationButtonVisibleRule) trueConditions++;
      if (propertiesHistoryButtonVisibleRule) trueConditions++;
      if (assetChassisNumberButtonVisibleRule) trueConditions++;
      return trueConditions === 1;
    }, [assetChassisNumberButtonVisibleRule, configurationButtonVisibleRule, propertiesHistoryButtonVisibleRule]);
    const onlyOneActionRuleIsVisible = checkIfOnlyOneActionRuleIsVisible();

    const getItemHierarchy = useCallback(() => {
      if (!hierarchy) return null;

      const { getHierarchyItem } = assetHierarchyFunctions(asset.document.hierarchy, hierarchy);
      return getHierarchyItem();
    }, [asset.document.hierarchy, hierarchy]);

    const mandatoryPropertiesConfigured = useSelector(
      state =>
        state.assetData.mandatoryPropertiesConfigured[
          `${document.itemId ?? ''}_${document.assetTypeId ?? ''}_${document.category ?? ''}_${document.classification ?? ''}`
        ]?.properties || [],
      shallowEqual
    );

    const getMandatoryFields = useMemo(() => {
      const emptyRequiredProps =
        (!isFacilityFrame &&
          document.properties?.filter(
            prop => !prop.value && prop.value !== 0 && (prop.mandatory || mandatoryPropertiesConfigured.includes(prop.propertyCode))
          )) ||
        [];

      mandatoryFinancedAssetFields.forEach(field => {
        if (!document[field.key]) emptyRequiredProps.push({ name: t(field.value) });
      });

      const hirarchyItem = getItemHierarchy();

      if (!hirarchyItem?.id) emptyRequiredProps.push({ name: t('OfferEdit.Asset.Info.Asset') });

      if (document.isSecondHand && !hasWorkingHours && !document.km)
        emptyRequiredProps.push({ name: t('OfferEdit.Asset.Info.AcquisitionKm') });

      if (document.isSecondHand && !document.buildYearAndMonth)
        emptyRequiredProps.push({ name: t('OfferEdit.Asset.Info.BuildYearAndMonth') });

      if (document.isSecondHand && hasWorkingHours && !document.workingHours)
        emptyRequiredProps.push({ name: t('OfferEdit.Asset.Info.WorkingHours') });

      if (document.isChassisNumberAvailable && !document.chassisNumber)
        emptyRequiredProps.push({ name: t('OfferEdit.Asset.Info.ChassisNumber') });

      if (workLocationIsMandatory) emptyRequiredProps.push({ name: t('OfferEdit.Asset.GeneralDetails.WorkLocation') });

      if (financedAssetDealerWrite && !isFacilityFrame) {
        mandatoryDealerAssetFields.forEach(field => {
          if (!document.partner.dealer || !document.partner.dealer[field.key]) emptyRequiredProps.push({ name: t(field.value) });
        });
      }

      if (financedAssetDealerWrite && document.partner.supplierType === supplierType.Other) {
        mandatorySupplierAssetFields.forEach(field => {
          if (!document.partner.supplier || !document.partner.supplier[field.key])
            emptyRequiredProps.push({
              name: field.key === 'registrationCode' ? `${t(mandatorySupplierAssetFields[0].value)} ${t(field.value)}` : t(field.value)
            });
        });
      }

      return emptyRequiredProps;
    }, [
      t,
      document,
      mandatoryPropertiesConfigured,
      financedAssetDealerWrite,
      hasWorkingHours,
      isFacilityFrame,
      getItemHierarchy,
      workLocationIsMandatory
    ]);

    const requiredFieldsRefs = useRef([]);
    requiredFieldsRefs.current = [];

    const addToRefs = (ref, elemName, elemType) => {
      const isEmptyMandatory = getMandatoryFields.find(el => el.name === elemName);

      if (ref && !requiredFieldsRefs.current.includes(ref) && isEmptyMandatory)
        requiredFieldsRefs.current.push({ ref: ref, type: elemType });
    };

    const focusRef = (ref, type = 'undefined') => {
      switch (type) {
        case TP_NUM:
        case TP_TEXT:
        case TP_DATE:
          ref.firstElementChild?.firstElementChild?.nextElementSibling?.firstElementChild?.focus();
          break;
        case COMBO:
        case SELECT:
        case SEARCH:
        case SEARCH_BIT:
          ref.firstElementChild?.firstElementChild?.firstElementChild?.nextElementSibling?.firstElementChild?.focus();
          break;
        default:
          break;
      }
    };

    const debouncedScrollAndFocus = debounce(fieldRef => {
      fieldRef.ref.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      focusRef(fieldRef.ref, fieldRef.type);
    }, 500);

    const handleClickToScroll = fieldName => {
      const fieldRef = requiredFieldsRefs.current.find(el => el.ref?.id?.toString() === fieldName);

      if (!fieldRef) return;

      if (document?.properties?.find(field => field?.name === fieldName)) {
        setExpandedCards(prevState => {
          return { ...prevState, assetPropertiesCardExpanded: true };
        });
      } else if (fieldName === 'Dealer' || fieldName === 'Supplier' || fieldName === 'Supplier CUI')
        setExpandedCards(prevState => {
          return { ...prevState, dealerCardExpanded: true };
        });

      debouncedScrollAndFocus(fieldRef);
    };

    const handleCardToggle = useCallback(
      cardExpanded => () => {
        setExpandedCards(prevState => {
          return { ...prevState, [cardExpanded]: !prevState[cardExpanded] };
        });
      },
      []
    );

    return (
      <form noValidate>
        <Grid container spacing={3} alignItems='center'>
          <OfferAssetWorkingLocationComponent
            asset={asset}
            onPropertyChange={onPropertyChange}
            processing={processing}
            disabled={rights.readOnlyFull || !financedAssetWrite}
            addToRefs={addToRefs}
          />
          <Grid item xs={12} lg={8}>
            <OfferAssetSecondHandComponent
              isSecondHand={document.isSecondHand}
              buildYearAndMonth={document.buildYearAndMonth}
              buildYearAndMonthError={validationInfo.buildYearAndMonth}
              secondHandProperty={secondHandProperty}
              onPropertyChange={onPropertyChange}
              maxMonths={maxMonths}
              processing={processing}
              selfPartnerName={selfPartnerName}
              addToRefs={addToRefs}
            />
            <Grid container spacing={3}>
              <AssetSearchComponent
                onPropertyChange={onPropertyChange}
                buildYearAndMonth={getDateValue(document.buildYearAndMonth)}
                showQuantity={true}
                quantity={document.quantity}
                quantityError={validationInfo.quantity}
                withIndividualAssetData={document.withIndividualAssetData}
                processing={processing}
                addToRefs={addToRefs}
              />

              <OfferAssetFinancialComponent
                acquisitionPriceWithoutVat={document.acquisitionPriceWithoutVat}
                reevaluationPriceWithoutVat={document.reevaluationPriceWithoutVat}
                currencyId={document.currencyId}
                acquisitionPriceWithVat={document.acquisitionPriceWithVat}
                acquisitionPriceWithoutVatError={validationInfo.acquisitionPriceWithoutVat}
                acquisitionPriceWithVatError={validationInfo.acquisitionPriceWithVat}
                currencies={currencies}
                onPropertyChange={onPropertyChange}
                processing={processing}
                addToRefs={addToRefs}
              />
            </Grid>
            <OfferAssetChassisNumberComponent
              isChassisNumberAvailable={document.isChassisNumberAvailable}
              chassisNumber={localChassisNumber}
              chassisNumberError={validationInfo.chassisNumber}
              onPropertyChange={onPropertyChange}
              processing={processing}
              onAssetDataChange={onAssetDataChange}
              addToRefs={addToRefs}
            />
            {document.remat && <OfferAssetRematComponent onPropertyChange={onPropertyChange} remat={document.remat} />}

            {document.firstOwner && (
              <OfferAssetFirstOwnerComponent
                onPropertyChange={onPropertyChange}
                firstOwner={document.firstOwner}
                processing={processing}
                selfPartnerName={selfPartnerName}
              />
            )}
          </Grid>
          <Grid item xs={12} lg={4}>
            {assetImage && (
              <Grid container justifyContent='center'>
                <Grid item xs={12} sm={10} md={8} lg={12} p={1} className='fileInput text-center'>
                  {assetImage && <OfferAssetImage src={assetImage} alt='...' />}
                </Grid>
              </Grid>
            )}
          </Grid>
          <Grid item container>
            <Grid item xs={12} lg={9} sx={{ order: underLg ? 2 : 1, pr: { xs: 0, lg: 2 } }}>
              <Grid container spacing={2}>
                {financedAssetDealerRead && (
                  <Grid item xs={12}>
                    <CollapseCard
                      id={'offerAssetCard_partnerCard'}
                      title={t('OfferEdit.Asset.Partner.Dealer')}
                      expanded={expandedCards.dealerCardExpanded}
                      onToggle={handleCardToggle('dealerCardExpanded')}
                      toggleOnHeaderClick
                    >
                      <OfferAssetPartnerComponent
                        partner={document.partner}
                        shouldUseUsersAsContactPersonsForDealer={document.shouldUseUsersAsContactPersonsForDealer}
                        onPropertyChange={addPartnerPrefix(onPropertyChange)}
                        loadDealers={loadDealers}
                        loadSuppliers={loadSuppliers}
                        localSupplier={localSupplier}
                        processing={processing}
                        loadPartnerContactInfo={loadPartnerContactInfo}
                        validationInfo={validationInfo.partner}
                        isSearchingRestrictedDealer={isSearchingRestrictedDealer}
                        addToRefs={addToRefs}
                      />
                    </CollapseCard>
                  </Grid>
                )}
                {!!document.itemId && (
                  <Grid item xs={12}>
                    <CollapseCard
                      id={'offerAssetCard_propertiesCard'}
                      title={t('OfferEdit.Asset.AssetProperties')}
                      expanded={expandedCards.assetPropertiesCardExpanded}
                      onToggle={handleCardToggle('assetPropertiesCardExpanded')}
                      toggleOnHeaderClick
                    >
                      <OfferAssetPropertiesComponent
                        properties={document.properties}
                        processing={processing}
                        onPropertyChange={addPropertiesPrefix(onPropertyChange)}
                        validationInfo={validationInfo.properties}
                        loadPropertyDetails={loadPropertyDetails}
                        addToRefs={addToRefs}
                      />
                    </CollapseCard>
                  </Grid>
                )}
                {enableAssetConfigurator && (
                  <Grid item xs={12}>
                    <CollapseCard id={'offerAssetCard_equipmentsCard'} title={t('OfferEdit.Asset.Equipments.Title')} toggleOnHeaderClick>
                      <OfferAssetEquipmentsComponent processing={processing} equipments={document.equipments} />
                    </CollapseCard>
                    <ExpansionPanel>
                      <ExpansionPanelSummary id={'offerAssetCard_equipmentsCard'} expandIcon={<ExpandMore />}>
                        <Typography variant='h6'>{t('OfferEdit.Asset.Equipments.Title')}</Typography>
                      </ExpansionPanelSummary>
                      <ExpansionPanelDetails></ExpansionPanelDetails>
                    </ExpansionPanel>
                  </Grid>
                )}
              </Grid>
            </Grid>
            <StickyGridWrapper item xs={12} lg={3} sx={{ order: underLg ? 1 : 2 }}>
              <StickyGrid container spacing={2} xs={12}>
                <Grid item xs={12}>
                  <OfferAssetRequiredPropertiesComponent
                    properties={getMandatoryFields}
                    handleClickToScroll={handleClickToScroll}
                    assetPropertiesAreLoaded={assetPropertiesAreLoaded}
                  />
                </Grid>
                <Grid item container xs={12} spacing={2} alignItems='center' justifyContent='space-between'>
                  <Grid item xs>
                    {!rights.isImage && (
                      <Button
                        id={'offerAssetCard_saveBtn'}
                        sx={{ width: 1 }}
                        color='primary'
                        variant='contained'
                        onClick={onSave}
                        disabled={
                          saving ||
                          rights.readOnlyFull ||
                          !rights.financedAssetWrite ||
                          !isDirty ||
                          !validationInfo.isValid ||
                          processing ||
                          !assetPropertiesAreLoaded ||
                          getMandatoryFields.length > 0
                        }
                      >
                        {saving ? t('Saving') : t('Save')}
                      </Button>
                    )}
                  </Grid>
                  <OfferAssetOptionsWrapper item xs='auto'>
                    {assetChassisNumberButtonVisibleRule &&
                      onlyOneActionRuleIsVisible &&
                      actionRulesMap.get('OfferAssetChassisNumberPopupContainer') && (
                        <Grid item xs='auto'>
                          <OfferAssetChassisNumberPopupContainer
                            onPropertyChange={onPropertyChange}
                            chassisNumberError={validationInfo.chassisNumber}
                            handleChassisNumberSave={handleChassisNumberSave}
                            handleCancelChassisNumberSave={handleCancelChassisNumberSave}
                            isValid={validationInfo.isValid}
                            processing={processing}
                            localChassisNumber={localChassisNumber}
                            chassisNumber={document.chassisNumber}
                            updating={updatingChassisNumber}
                          />
                        </Grid>
                      )}
                    {assetChassisNumberButtonVisibleRule && !onlyOneActionRuleIsVisible && openOptions && (
                      <Grow in={openOptions}>
                        <Grid item xs='auto'>
                          <OfferAssetChassisNumberPopupContainer
                            onPropertyChange={onPropertyChange}
                            chassisNumberError={validationInfo.chassisNumber}
                            handleChassisNumberSave={handleChassisNumberSave}
                            handleCancelChassisNumberSave={handleCancelChassisNumberSave}
                            isValid={validationInfo.isValid}
                            processing={processing}
                            localChassisNumber={localChassisNumber}
                            chassisNumber={document.chassisNumber}
                            updating={updatingChassisNumber}
                          />
                        </Grid>
                      </Grow>
                    )}
                    {configurationButtonVisibleRule &&
                      onlyOneActionRuleIsVisible &&
                      actionRulesMap.get('offerAssetCard_assetActions_equipmentsConfigurator') && (
                        <Grid item xs='auto'>
                          <Tooltip title={t('Tooltips.Configurator')} placement='bottom'>
                            <Fab
                              id={'offerAssetCard_assetActions_equipmentsConfigurator'}
                              color='primary'
                              onClick={openConfigurator}
                              size='small'
                            >
                              <Build />
                            </Fab>
                          </Tooltip>
                        </Grid>
                      )}
                    {configurationButtonVisibleRule && !onlyOneActionRuleIsVisible && openOptions && (
                      <Grow in={openOptions}>
                        <Grid item xs='auto'>
                          <Tooltip title={t('Tooltips.Configurator')} placement='bottom'>
                            <Fab
                              id={'offerAssetCard_assetActions_equipmentsConfigurator'}
                              color='primary'
                              onClick={openConfigurator}
                              size='small'
                            >
                              <Build />
                            </Fab>
                          </Tooltip>
                        </Grid>
                      </Grow>
                    )}
                    {propertiesDuplicateVisibleRule && !rights.isImage && asset?.document?.documentDetailId && !duplicating && (
                      <Grow in={openOptions}>
                        <Grid item xs='auto'>
                          <Tooltip title={t('Duplicate')} placement='bottom'>
                            <Grid item xs>
                              <Fab id={'offerAssetCard_duplicateBtn'} color='primary' onClick={onDuplicate} size='small'>
                                <FileCopy />
                              </Fab>
                            </Grid>
                          </Tooltip>
                        </Grid>
                      </Grow>
                    )}
                    {propertiesHistoryButtonVisibleRule &&
                      onlyOneActionRuleIsVisible &&
                      actionRulesMap.get('offerAssetCard_assetActions_propertiesHistory') && (
                        <Grid item xs='auto'>
                          <Tooltip title={t('OfferEdit.Asset.Properties.History')} placement='bottom'>
                            <Fab
                              id={'offerAssetCard_assetActions_propertiesHistory'}
                              color='primary'
                              onClick={openPropertiesHistory}
                              size='small'
                            >
                              <History />
                            </Fab>
                          </Tooltip>
                        </Grid>
                      )}
                    {propertiesHistoryButtonVisibleRule && !onlyOneActionRuleIsVisible && openOptions && (
                      <Grow in={openOptions}>
                        <Grid item xs='auto'>
                          <Tooltip title={t('OfferEdit.Asset.Properties.History')} placement='bottom'>
                            <Fab
                              id={'offerAssetCard_assetActions_propertiesHistory'}
                              color='primary'
                              onClick={openPropertiesHistory}
                              size='small'
                            >
                              <History />
                            </Fab>
                          </Tooltip>
                        </Grid>
                      </Grow>
                    )}
                    {actionsVisibleRules && !onlyOneActionRuleIsVisible && (
                      <Grid item xs='auto'>
                        {!openOptions ? (
                          <Tooltip title={t('Tooltips.ShowAssetActions')} placement='bottom'>
                            <Fab id={'offerAssetCard_assetActions_showBtn'} color='primary' onClick={handleOpenOptions} size='small'>
                              <List />
                            </Fab>
                          </Tooltip>
                        ) : (
                          <Tooltip title={t('Tooltips.HideAssetActions')} placement='bottom'>
                            <Fab id={'offerAssetCard_assetActions_hideBtn'} color='primary' onClick={handleCloseOptions} size='small'>
                              <Remove />
                            </Fab>
                          </Tooltip>
                        )}
                      </Grid>
                    )}
                  </OfferAssetOptionsWrapper>
                </Grid>
              </StickyGrid>
            </StickyGridWrapper>
          </Grid>
        </Grid>
      </form>
    );
  }
);

OfferAssetComponent.displayName = 'OfferAssetComponent';

OfferAssetComponent.propTypes = {
  t: PropTypes.func.isRequired,
  asset: PropTypes.object,
  hierarchy: PropTypes.object,
  assetImage: PropTypes.string,
  assetTypes: PropTypes.array.isRequired,
  currencies: PropTypes.array.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onDuplicate: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired,
  duplicating: PropTypes.bool.isRequired,
  loadPropertyDetails: PropTypes.func.isRequired,
  loadDealers: PropTypes.func.isRequired,
  loadSuppliers: PropTypes.func.isRequired,
  localSupplier: PropTypes.object,
  loadPartnerContactInfo: PropTypes.func.isRequired,
  rights: PropTypes.object.isRequired,
  isDirty: PropTypes.bool.isRequired,
  isSearchingRestrictedDealer: PropTypes.bool.isRequired,
  handleChassisNumberSave: PropTypes.func.isRequired,
  handleCancelChassisNumberSave: PropTypes.func.isRequired,
  updatingChassisNumber: PropTypes.bool,
  localChassisNumber: PropTypes.string,
  updateAssetChassisNo: PropTypes.bool.isRequired,
  financedAssetWrite: PropTypes.bool.isRequired,
  handleOpenOptions: PropTypes.func.isRequired,
  handleCloseOptions: PropTypes.func.isRequired,
  openOptions: PropTypes.bool.isRequired,
  openConfigurator: PropTypes.func,
  openPropertiesHistory: PropTypes.func,
  maxMonths: PropTypes.number,
  enableConfiguratorButton: PropTypes.bool.isRequired,
  selfPartnerName: PropTypes.string.isRequired,
  editRight: PropTypes.bool.isRequired,
  onAssetDataChange: PropTypes.func.isRequired,
  processing: PropTypes.bool,
  hasWorkingHours: PropTypes.bool,
  isFacilityFrame: PropTypes.bool,
  assetPropertiesAreLoaded: PropTypes.bool.isRequired,
  defaultValue: PropTypes.string
};

export default compose(withTranslation(), withRights, withCustomComponent('OfferAssetComponent'))(OfferAssetComponent);
